/**
 * 🔍 ENHANCED VERIFICATION ITEM - Better display for verification records
 */

import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  LinearProgress,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import {
  Person,
  DirectionsCar,
  Security,
  Badge as BadgeIcon,
  Fingerprint,
  Description,
  CheckCircle,
  Schedule,
  Cancel,
  Warning,
  Edit,
  Visibility,
  Note,
  MoreVert,
  AttachFile,
  CameraAlt,
  History,
  Download,
  Flag,
  Delete,
} from '@mui/icons-material';

import { VerificationStatusBadge } from '@/components/users/VerificationStatusBadge';
import type { VerificationRecord } from '@/services/verificationService';
import { verificationService } from '@/services/verificationService';
import { VerificationStatus } from '@/types';
import toast from 'react-hot-toast';

interface EnhancedVerificationItemProps {
  verification: VerificationRecord;
  onViewDetails: (id: string) => void;
  onViewPhotos?: (userId: string) => void;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
}

export const EnhancedVerificationItem: React.FC<EnhancedVerificationItemProps> = ({
  verification,
  onViewDetails,
  onViewPhotos,
  onApprove,
  onReject
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  // Dialog states
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewHistory = () => {
    handleMenuClose();
    setHistoryDialogOpen(true);
  };

  const handleDownloadDocs = () => {
    handleMenuClose();

    // Collect all document URLs
    const docs = [];
    if (verification.data?.driverLicense?.frontImageUrl) docs.push(verification.data.driverLicense.frontImageUrl);
    if (verification.data?.driverLicense?.backImageUrl) docs.push(verification.data.driverLicense.backImageUrl);
    if (verification.data?.vehicleInfo?.registrationDocumentUrl) docs.push(verification.data.vehicleInfo.registrationDocumentUrl);

    if (docs.length === 0) {
      // Show no docs dialog instead of alert
      return;
    }

    // For now, open documents in new tabs (in production, you'd want a ZIP download)
    docs.forEach((url, index) => {
      setTimeout(() => window.open(url, '_blank'), index * 500);
    });
  };

  const handleFlagIssue = () => {
    handleMenuClose();
    setFlagDialogOpen(true);
  };

  const handleFlagConfirm = async () => {
    if (flagReason.trim()) {
      try {

        const response = await verificationService.flagVerification(verification.id, flagReason);

        if (response.success) {
          toast.success(response.message || 'Verification flagged successfully!', {
            duration: 4000,
            icon: '🚩',
          });

          setFlagDialogOpen(false);
          setFlagReason('');
        } else {
          toast.error(response.error || 'Failed to flag verification. Please try again.');
        }
      } catch (error) {
        console.error('Error flagging verification:', error);
        toast.error('Failed to flag verification. Please try again.');
      }
    }
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {

      const response = await verificationService.deleteVerification(verification.id, 'Admin deletion');

      if (response.success) {
        toast.success(response.message || 'Verification deleted successfully!', {
          duration: 4000,
          icon: '🗑️',
        });

        setDeleteDialogOpen(false);

        // Optionally refresh the list or remove the item from view
        // You might want to call a parent component's refresh function here
      } else {
        toast.error(response.error || 'Failed to delete verification. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting verification:', error);
      toast.error('Failed to delete verification. Please try again.');
    }
  };

  const getDisplayName = (verification: VerificationRecord): string => {
    const personalInfo = verification.data?.personalInfo;

    // Use real name from identity verification
    if (personalInfo?.firstName && personalInfo?.lastName) {
      return `${personalInfo.firstName} ${personalInfo.lastName}`;
    }
    
    // Try to extract name from other fields
    if (personalInfo?.firstName) {
      return personalInfo.firstName;
    }
    
    // Create a meaningful name from user ID or purpose
    if (verification.purpose === 'driver_application') {
      return 'Driver Application';
    }
    
    if (verification.purpose === 'customer_verification') {
      return 'Customer Verification';
    }
    
    return `Verification ${verification.id.slice(-6).toUpperCase()}`;
  };

  const getVerificationType = (purpose: string): {
    label: string;
    icon: React.ReactNode;
    color: string;
  } => {
    switch (purpose) {
      case 'driver_application':
        return {
          label: 'Driver Application',
          icon: <DirectionsCar />,
          color: 'primary.main'
        };
      case 'customer_verification':
        return {
          label: 'Identity Verification',
          icon: <Person />,
          color: 'secondary.main'
        };
      default:
        return {
          label: 'General Verification',
          icon: <BadgeIcon />,
          color: 'info.main'
        };
    }
  };

  const getDocumentCount = (verification: VerificationRecord): number => {
    const data = verification.data;
    let count = 0;
    
    // Count actual uploaded documents
    if (data?.driverLicense?.licenseNumber) count++;
    if (data?.driverLicense?.frontImageUrl) count++;
    if (data?.driverLicense?.backImageUrl) count++;
    if (data?.personalInfo?.nin) count++;
    if (data?.vehicleInfo?.registrationDocumentUrl) count++;
    if (data?.vehicleInfo?.insuranceDocumentUrl) count++;
    if (data?.vehicleInfo?.vehiclePhotoUrls?.length) count += data.vehicleInfo.vehiclePhotoUrls.length;
    
    return count;
  };

  const getDocumentTypes = (verification: VerificationRecord): string[] => {
    const data = verification.data;
    const types: string[] = [];
    
    if (data?.driverLicense?.licenseNumber) types.push('Driver License');
    if (data?.personalInfo?.nin) types.push('National ID');
    if (data?.vehicleInfo?.registrationDocumentUrl) types.push('Vehicle Registration');
    if (data?.vehicleInfo?.insuranceDocumentUrl) types.push('Insurance');
    if (data?.vehicleInfo?.vehiclePhotoUrls?.length) types.push('Vehicle Photos');
    if (data?.bankingInfo?.accountNumber) types.push('Banking Info');
    
    return types;
  };

  const getStatusFromVerification = (status: VerificationStatus, completionPercentage: number) => {
    if (status === VerificationStatus.APPROVED) {
      return 'complete';
    }
    if (status === VerificationStatus.PENDING_REVIEW && completionPercentage === 100) {
      return 'complete';
    }
    if (status === VerificationStatus.PENDING_REVIEW) {
      return 'pending_review';
    }
    if (status === VerificationStatus.REJECTED) {
      return 'rejected';
    }
    if (status === VerificationStatus.DRAFT) {
      return 'draft';
    }
    return 'pending_review';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const displayName = getDisplayName(verification);
  const verificationType = getVerificationType(verification.purpose);
  const documentCount = getDocumentCount(verification);
  const documentTypes = getDocumentTypes(verification);
  const priority = verification.completionPercentage < 50 ? 'High' : 
                   verification.completionPercentage < 80 ? 'Medium' : 'Low';
  
  const statusType = getStatusFromVerification(verification.status, verification.completionPercentage);

  return (
    <TableRow hover>
      {/* Type */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ 
            bgcolor: verificationType.color, 
            width: 32, 
            height: 32 
          }}>
            {verificationType.icon}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {verificationType.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {verification.id.slice(-6).toUpperCase()}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* User */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 32, height: 32 }}>
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {verification.purpose === 'driver_application' ? 'Driver' : 'Customer'}
            </Typography>
            {verification.data?.personalInfo?.nin && (
              <Typography variant="caption" color="text.secondary" display="block">
                NIN: ***{verification.data.personalInfo.nin.slice(-4)}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>

      {/* Status */}
      <TableCell>
        <Stack spacing={1} alignItems="flex-start">
          <VerificationStatusBadge
            status={statusType as any}
            completionPercentage={verification.completionPercentage}
            type={verification.purpose === 'driver_application' ? 'driver' : 'identity'}
            showPercentage={true}
            size="small"
          />
          
          {/* Progress bar for incomplete items */}
          {verification.completionPercentage < 100 && (
            <Box sx={{ width: '100%', maxWidth: 120 }}>
              <LinearProgress
                variant="determinate"
                value={verification.completionPercentage}
                color={verification.completionPercentage > 75 ? 'success' : 
                       verification.completionPercentage > 50 ? 'warning' : 'error'}
                sx={{ height: 4, borderRadius: 2 }}
              />
            </Box>
          )}
        </Stack>
      </TableCell>

      {/* Priority */}
      <TableCell>
        <Chip
          label={priority}
          size="small"
          color={getPriorityColor(priority) as any}
          variant="outlined"
        />
      </TableCell>

      {/* Submitted */}
      <TableCell>
        <Typography variant="body2">
          {new Date(verification.submittedAt || verification.createdAt).toLocaleDateString()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(verification.submittedAt || verification.createdAt).toLocaleTimeString()}
        </Typography>
      </TableCell>

      {/* Documents */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Badge badgeContent={documentCount} color="primary">
            <AttachFile sx={{ fontSize: 16 }} />
          </Badge>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {documentCount} docs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {documentTypes.slice(0, 2).map((type, index) => (
            <Chip
              key={index}
              label={type}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.6rem', height: 18 }}
            />
          ))}
          {documentTypes.length > 2 && (
            <Typography variant="caption" color="text.secondary">
              +{documentTypes.length - 2} more
            </Typography>
          )}
        </Box>
      </TableCell>

      {/* Actions */}
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => onViewDetails(verification.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>

          {onViewPhotos && verification.userId && (
            <Tooltip title="View Photos">
              <IconButton size="small" onClick={() => onViewPhotos(verification.userId)}>
                <CameraAlt fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          )}

          {verification.adminNotes && (
            <Tooltip title="Has Notes">
              <IconButton size="small">
                <Note fontSize="small" color="warning" />
              </IconButton>
            </Tooltip>
          )}
          
          {(verification.status === VerificationStatus.PENDING_REVIEW &&
            verification.completionPercentage === 100) && (
            <>
              {onApprove && (
                <Tooltip title="Approve">
                  <IconButton size="small" onClick={() => onApprove(verification.id)} color="success">
                    <CheckCircle fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onReject && (
                <Tooltip title="Reject">
                  <IconButton size="small" onClick={() => onReject(verification.id)} color="error">
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          <Tooltip title="More actions">
            <IconButton size="small" onClick={handleMenuClick}>
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Actions Menu */}
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleViewHistory}>
              <ListItemIcon>
                <History fontSize="small" />
              </ListItemIcon>
              <ListItemText>View History</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleDownloadDocs}>
              <ListItemIcon>
                <Download fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download Documents</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleFlagIssue}>
              <ListItemIcon>
                <Flag fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText>Flag Issue</ListItemText>
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </Menu>

          {/* History Dialog */}
          <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Verification History</DialogTitle>
            <DialogContent>
              <DialogContentText>
                <strong>Verification ID:</strong> {verification.id}
                <br />
                <strong>User:</strong> {getDisplayName(verification)}
                <br />
                <strong>Status:</strong> {verification.status}
              </DialogContentText>
              <Alert severity="info" sx={{ mt: 2 }}>
                History tracking coming soon! This will show all changes, reviews, and status updates for this verification.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Flag Issue Dialog */}
          <Dialog open={flagDialogOpen} onClose={() => setFlagDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Flag Verification Issue</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Why are you flagging this verification? Please provide a detailed reason.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                multiline
                rows={4}
                label="Reason for flagging"
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
                placeholder="Describe the issue with this verification..."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setFlagDialogOpen(false);
                setFlagReason('');
              }}>
                Cancel
              </Button>
              <Button
                onClick={handleFlagConfirm}
                variant="contained"
                color="warning"
                disabled={!flagReason.trim()}
              >
                Flag Issue
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: 'error.main' }}>⚠️ Delete Verification?</DialogTitle>
            <DialogContent>
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>WARNING:</strong> This action cannot be undone!
              </Alert>
              <DialogContentText>
                <strong>Verification ID:</strong> {verification.id}
                <br />
                <strong>User:</strong> {getDisplayName(verification)}
                <br />
                <strong>Status:</strong> {verification.status}
                <br /><br />
                Are you absolutely sure you want to permanently delete this verification record?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                variant="contained"
                color="error"
              >
                Delete Permanently
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </TableCell>
    </TableRow>
  );
};